import { Anchor, Box, Checkbox, ColorSwatch, Group, Pagination, Stack, Table, Text, Tooltip } from "@mantine/core";
import {
  ArrowBendUpRightIcon, ArrowRightIcon, EyeIcon, GavelIcon, XIcon
} from "@phosphor-icons/react";
import { i18n } from "@dehub/theme";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import type { IReturnSummary, ReturnSubStatus } from "../../apis/return";
import { currencyFormat } from "../../utils/currency";
import { dateFormat } from "../../utils/date";
import { DeliveryRowAction } from "../delivery/action-controls";
import { Dot, DotPill, TableFrame } from "../delivery/atoms";
import { TRUST_LEVEL_TONE } from "./trust-level-badge";
import { ListState } from "../ui";
import { ReturnStatusBadge, reasonLabel, subReasonLabel } from "./atoms";
import { ReturnAction, ReturnActionState, useReturnActions } from "./return-actions";

// Lining figures so codes/amounts align vertically down a column.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

interface Props {
  rows: IReturnSummary[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  loading?: boolean;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  /** Total matching rows (from pagination.total) — drives the "1-N / TOTAL" range text. */
  total?: number;
  /** Page size (from pagination.limit) — used to compute the visible range. */
  limit?: number;
  /** Tenant dispute ceiling (meta.disputeMaxRounds) — denominator of the dispute progress. Default 3. */
  disputeMax?: number;
  /** When true, renders the disputeNotes column (the round is already shown as dots under the status). Default: false. */
  showDispute?: boolean;
  /** Row checkboxes (bulk selection). Pages without a bulk toolbar pass false. Default: true. */
  selectable?: boolean;
  /** §9 — when provided, the DISPUTED escalate row action calls this instead of opening the detail. */
  onManagerEscalate?: (r: IReturnSummary) => void;
  /** §19 — when provided, the REFUND_PENDING confirm row action opens the refund dialog inline. */
  onConfirmRefund?: (r: IReturnSummary) => void;
  /** Empty-state copy (the page knows which tab / filter is active). */
  emptyTitle?: ReactNode;
  emptyDescription?: ReactNode;
}

// §9.1 — humanised sub-state label rendered under the status badge.
const subStatusLabel = (v: ReturnSubStatus | string) => i18n.t(`returns:listTable.subStatus.${v}`, { defaultValue: "-" });

/** Request type in plain words: «Зөвшөөрөлтэй», «Шууд», «Байршуулалт». */
export function returnTypeLabel(r: { isConsignmentReturn?: boolean | null; requestType?: string | null }): string {
  if (r.isConsignmentReturn) return i18n.t("returns:listTable.type.CONSIGNMENT");
  if (r.requestType === "RETURN" || r.requestType === "SIMPLE") return i18n.t(`returns:listTable.type.${r.requestType}`);
  return "-";
}

/** The row's next step(s) by status, in priority order. Only those that fit the user's side are shown. */
const ROW_ACTIONS: Record<string, ReturnAction[]> = {
  REQUESTED                 : ["propose", "cancel"],
  PROPOSED                  : ["accept", "reject"],
  DISPUTED                  : ["propose", "escalate"],
  AWAITING_PICKUP_SCHEDULE  : ["schedulePickup"],
  AWAITING_SHIPMENT_INFO    : ["markShipped"],
  IN_TRANSIT                : ["reportTransit"],
  IN_TRANSIT_EXCEPTION      : ["resolveTransit"],
  AWAITING_WAREHOUSE_RECEIPT: ["warehouseReceive"],
  REFUND_PENDING            : ["confirmRefund"]
};

const ACTION_ICON: Partial<Record<ReturnAction, ReactNode>> = {
  escalate: <ArrowBendUpRightIcon />,
  managerOverride: <GavelIcon />,
  cancel  : <XIcon />,
  reject  : <XIcon />
};

// Dispute progress — filled swatches for completed rounds, neutral for the remainder.
function DisputeDots({ round, max }: { round?: number; max?: number }) {
  const safeMax = Number.isFinite(max) && (max as number) > 0 ? Math.floor(max as number) : 3;
  const safeRound = Number.isFinite(round) && (round as number) > 0
    ? Math.min(Math.floor(round as number), safeMax)
    : 0;
  return (
    <Group gap={4} wrap="nowrap" align="center">
      {Array.from({ length: safeMax }, (_, i) => (
        <ColorSwatch
          key={i}
          size={9}
          withShadow={false}
          color={i < safeRound ? "var(--mantine-color-yellow-6)" : "var(--mantine-color-gray-3)"}
        />
      ))}
      <Text size="sm" c="var(--nw-text-2)" style={TNUM}>{safeRound}/{safeMax}</Text>
    </Group>
  );
}

// SLA line: days remaining vs overdue.
function SlaText({ days }: { days: number }) {
  const { t } = useTranslation("returns");
  return (
    <Text size="sm" c="var(--nw-text-2)" style={TNUM}>
      {days > 0 ? t("listTable.daysRemaining", { count: days }) : t("listTable.daysOverdue", { count: Math.abs(days) })}
    </Text>
  );
}

/**
 * First «Үйлдэл» column: the row's next step as a grey subtle icon (canvas Ord_ReturnList «→»)
 * plus «Дэлгэрэнгүй». A step the user may not take stays visible, aria-disabled, with the reason.
 */
function RowActions({ r, stateOf, disputeMax, onManagerEscalate, onConfirmRefund }: {
  r: IReturnSummary;
  disputeMax: number;
  stateOf: (a: ReturnAction, s: IReturnSummary) => ReturnActionState;
  onManagerEscalate?: (r: IReturnSummary) => void;
  onConfirmRefund?: (r: IReturnSummary) => void;
}) {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const detailTo = `/returns/${r.returnRequestId}`;

  const run = (action: ReturnAction) => {
    if (action === "escalate" && onManagerEscalate) return onManagerEscalate(r);
    if (action === "confirmRefund" && onConfirmRefund) return onConfirmRefund(r);
    if (action === "warehouseReceive") return navigate(`${detailTo}/warehouse-receipt`);
    // The detail page opens the matching dialog / tab (see ReturnDetailPage `openAction`).
    navigate(detailTo, { state: { openAction: action } });
  };

  // O199 — a dispute that used up its rounds can only be settled by the manager decision.
  const base = ROW_ACTIONS[r.status] ?? [];
  const actions = r.status === "DISPUTED" && r.disputeCount >= disputeMax
    ? [base[0], "managerOverride" as ReturnAction, ...base.slice(1)]
    : base;
  const steps = actions
    .map((a) => ({ action: a, state: stateOf(a, r) }))
    .filter((x) => x.state.applies)
    .slice(0, 3);

  return (
    <Group gap="xs" wrap="nowrap">
      {steps.map(({ action, state }, i) => (
        <DeliveryRowAction
          key={action}
          label={t(`actions.label.${action}`)}
          icon={i === 0 ? <ArrowRightIcon /> : ACTION_ICON[action] ?? <ArrowRightIcon />}
          state={{ allowed: state.enabled, reason: state.reason }}
          onClick={() => run(action)}
        />
      ))}
      <DeliveryRowAction label={t("breadcrumb.detail")} icon={<EyeIcon />} onClick={() => navigate(detailTo)} />
    </Group>
  );
}

export function ReturnListTable({
  rows, page, totalPages, onPageChange, selectedIds, onSelectionChange, loading,
  total, limit, disputeMax = 3, showDispute = false, selectable = true, onManagerEscalate, onConfirmRefund,
  emptyTitle, emptyDescription
}: Props) {
  const { t } = useTranslation("returns");
  const { stateOf } = useReturnActions({ disputeMax });

  if (!rows.length) {
    return (
      <TableFrame>
        {loading ? (
          <ListState state="loading" columns={8} />
        ) : (
          <ListState
            state="empty"
            title={emptyTitle ?? t("listTable.empty")}
            description={emptyDescription ?? t("listTable.emptyHint")}
          />
        )}
      </TableFrame>
    );
  }

  const pageIds = rows.map((r) => r.returnRequestId);
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const someSelected = pageIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange(selectedIds.filter((id) => !pageIds.includes(id)));
    } else {
      const next = [...selectedIds];
      for (const id of pageIds) {
        if (!next.includes(id)) next.push(id);
      }
      onSelectionChange(next);
    }
  };

  const handleToggleRow = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((s) => s !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  // "1-N / TOTAL" range — only when the caller supplies the total count.
  const effectiveLimit = limit ?? rows.length;
  const rangeStart = total != null ? (page - 1) * effectiveLimit + 1 : 0;
  const rangeEnd = total != null ? Math.min(page * effectiveLimit, total) : 0;
  const safeTotalPages = Math.max(1, totalPages);

  const footer = (
    <>
      {total != null ? <Text span style={TNUM}>{rangeStart}-{rangeEnd} / {total}</Text> : <Box />}
      <Group gap="md" wrap="nowrap">
        {safeTotalPages > 1 ? (
          <Pagination value={page} total={safeTotalPages} onChange={onPageChange} size="sm" />
        ) : null}
        <Text span style={TNUM}>{t("listTable.pageOf", { page, total: safeTotalPages })}</Text>
      </Group>
    </>
  );

  return (
    <TableFrame footer={footer}>
      <Table.ScrollContainer minWidth={showDispute ? 1000 : 1080}>
        <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
          <Table.Thead>
            <Table.Tr>
              {selectable && (
                <Table.Th w={44}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleToggleAll}
                    aria-label={t("listTable.selectAll")}
                  />
                </Table.Th>
              )}
              <Table.Th>{t("listTable.col.action")}</Table.Th>
              <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
              {!showDispute && <Table.Th>{t("detail.field.order")}</Table.Th>}
              <Table.Th>{t("filters.buyer")}</Table.Th>
              <Table.Th>{t("filters.reasonLabel")}</Table.Th>
              {!showDispute && <Table.Th>{t("filters.type")}</Table.Th>}
              <Table.Th ta="right">{t("listTable.col.amount")}</Table.Th>
              <Table.Th>{t("listTable.col.status")}</Table.Th>
              <Table.Th>{t("listTable.col.trust")}</Table.Th>
              <Table.Th>{t("detail.field.date")}</Table.Th>
              {showDispute && <Table.Th>{t("listTable.col.disputeNotes")}</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => {
              const hasSla = r.slaDeadline != null && r.slaDaysRemaining != null;
              const selected = selectedIds.includes(r.returnRequestId);
              return (
                <Table.Tr key={r.returnRequestId} data-selected={selected || undefined}>
                  {selectable && (
                    <Table.Td>
                      <Checkbox
                        checked={selected}
                        onChange={() => handleToggleRow(r.returnRequestId)}
                        aria-label={t("listTable.selectRow", { number: r.returnNumber })}
                      />
                    </Table.Td>
                  )}
                  <Table.Td>
                    <RowActions r={r} stateOf={stateOf} disputeMax={disputeMax} onManagerEscalate={onManagerEscalate} onConfirmRefund={onConfirmRefund} />
                  </Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={`/returns/${r.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" style={{ whiteSpace: "nowrap" }} translate="no">
                      {r.returnNumber}
                    </Anchor>
                    {/* Disputes (canvas Ord_Disputes): the order number sits under the RET number. */}
                    {showDispute && (
                      <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" translate="no" style={{ whiteSpace: "nowrap" }}>{r.orderNumber || "-"}</Text>
                    )}
                  </Table.Td>
                  {!showDispute && (
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" translate="no" style={{ whiteSpace: "nowrap" }}>{r.orderNumber || "-"}</Text>
                    </Table.Td>
                  )}
                  <Table.Td maw={220}>
                    <Text size="sm" lineClamp={2}>{r.buyerBusinessName || "-"}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{reasonLabel(r.returnReason) || "-"}</Text>
                    {r.subReason ? (
                      <Text size="sm" c="var(--nw-text-2)">{t("listTable.subPrefix", { label: subReasonLabel(r.subReason) })}</Text>
                    ) : null}
                  </Table.Td>
                  {!showDispute && (
                    <Table.Td>
                      <Text size="sm">{returnTypeLabel(r)}</Text>
                    </Table.Td>
                  )}
                  <Table.Td ta="right">
                    <Text ff="monospace" fz="var(--nw-fz-mono)" style={{ ...TNUM, whiteSpace: "nowrap" }}>
                      {currencyFormat(r.totalRequestedAmount ?? 0)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4} align="flex-start">
                      <ReturnStatusBadge status={r.status} />
                      {r.status === "DISPUTED" ? (
                        <DisputeDots round={r.disputeRound ?? r.disputeCount} max={disputeMax} />
                      ) : (
                        <>
                          {r.subStatus ? <Text size="sm" c="var(--nw-text-2)">{subStatusLabel(r.subStatus)}</Text> : null}
                          {hasSla ? <SlaText days={r.slaDaysRemaining as number} /> : null}
                        </>
                      )}
                      {r.escalatedAt ? (
                        <DotPill tone="grey">{t("listTable.escalated", { date: dateFormat(r.escalatedAt) })}</DotPill>
                      ) : null}
                    </Stack>
                  </Table.Td>
                  {/* Ord_Disputes «● 16 Өндөр эрсдэлтэй»: tier dot, score, tier name. */}
                  <Table.Td>
                    {r.trustScore != null || r.trustTier ? (
                      <Group gap={6} wrap="nowrap">
                        {r.trustTier && <Dot tone={TRUST_LEVEL_TONE[r.trustTier] ?? "grey"} />}
                        {r.trustScore != null && (
                          <Text ff="monospace" fz="var(--nw-fz-mono)" style={TNUM}>
                            {Math.round(r.trustScore)}
                          </Text>
                        )}
                        {r.trustTier && <Text fz={14}>{t(`trustLevel.${r.trustTier}`, { defaultValue: "-" })}</Text>}
                      </Group>
                    ) : (
                      <Text fz={14}>-</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" fz="var(--nw-fz-mono)" style={{ ...TNUM, whiteSpace: "nowrap" }}>{dateFormat(r.requestedAt)}</Text>
                  </Table.Td>
                  {showDispute && (
                    <Table.Td maw={260}>
                      <Tooltip label={r.disputeNotes ?? ""} disabled={!r.disputeNotes} multiline maw={300}>
                        <Text size="sm" lineClamp={2}>{r.disputeNotes || "-"}</Text>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </TableFrame>
  );
}
